import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import '../../App.css';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { DataTable } from './datatable';
import DatatableSortButton from './datatableSortButton';

export const columns = [
    {
        accessorKey: "name",
        header: ({ column }) => <DatatableSortButton column={column}>Gårdsnamn</DatatableSortButton>,
        cell: info => {
            const farm = info.row.original;
            return <><Link to={`/farms/${farm.id}`} className='text-blue-600 font-medium hover:underline'>{farm.name}</Link> {farm?.archived_at && <Badge variant='destructive' className='ml-3'>Arkiverad</Badge>}</>
        }
    },
];

export function FarmsDatatable({ farms, isLoading }) {
    const [sorting, setSorting] = useState([{ id: 'name', desc: false }]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [filterInput, setFilterInput] = useState("");
    const [showArchived, setShowArchived] = useState(false);

    const handleArchivedStatus = () => {
        setShowArchived(prev => !prev);
    };

    const filteredFarms = useMemo(() => {
        const result = farms.filter(farm => {
            const isArchived = (farm.archived_at != null);

            // Always active farms
            if (!isArchived) {
                return true;
            }
            // Include archived farms if the checkbox is checked
            if (isArchived && showArchived) {
                return true;
            }
            // Otherwise, exclude the farms
            return false;
        });

        return result;
    }, [farms, showArchived]); // Dependencies for memoization

    // Delay function before filtering to reduce bounce
    useEffect(() => {
        const handler = setTimeout(() => {
            table.getColumn("name")?.setFilterValue(filterInput);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [filterInput]);

    const table = useReactTable({
        data: filteredFarms.length > 0 ? filteredFarms : [], // Ensure data is always an array
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            columnFilters,
        },
    });

    return (
        <>
            <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 py-3'>
                <div className='grid grid-cols-2 gap-3'>
                    <div className='flex flex-col gap-2 w-full'>
                        <Label htmlFor='searchFarm'>Gård</Label>
                        <Input
                            id='searchFarm'
                            placeholder="Sök gård..."
                            value={filterInput}
                            disabled={isLoading || farms.length === 0}
                            onChange={event => setFilterInput(event.target.value)}
                            autoComplete='off'
                        />
                    </div>
                    <div className='flex items-center gap-2'>
                        <Label className='flex gap-2 items-center'>
                            <Checkbox
                                checked={showArchived}
                                onCheckedChange={handleArchivedStatus}
                                disabled={isLoading || filteredFarms.length === 0} />
                            Inkludera arkiverade gårdar
                        </Label>
                    </div>
                </div>
            </div>
            <DataTable table={table} isLoading={isLoading} columns={columns} skeletonRows={10} />
        </>
    );
}